import React from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastProvider } from "@/components/ui/toast-clean";
import { BootstrapProvider } from "@/components/providers/BootstrapProvider";
import { NavBackProvider } from "@/contexts/NavBackContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import AppRouter from "@/pages/app/AppRouter";
import "@/index.css";
import "@/config/i18n"; // 初始化 i18n

// 在 React 渲染前初始化主题，避免闪烁
function initTheme() {
  const stored = localStorage.getItem("app-theme");
  let theme: "light" | "dark";

  if (stored === "light" || stored === "dark") {
    theme = stored;
  } else {
    // system 或未设置时跟随系统
    theme = window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }

  const root = document.documentElement;
  root.classList.remove("theme-vercel-light", "theme-vercel-dark", "dark");
  root.classList.add(`theme-vercel-${theme}`);
  if (theme === "dark") {
    root.classList.add("dark");
  }
}
initTheme();

// 设置 CSRF Token 和语言头
import axios from "axios";
import i18n from "@/config/i18n";

axios.defaults.headers.common["X-CSRF-Token"] =
  document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ||
  "";

// 设置 Accept-Language 头，根据当前 i18n 语言
const updateAcceptLanguage = () => {
  axios.defaults.headers.common["Accept-Language"] = i18n.language || "zh";
};
updateAcceptLanguage();

// 监听语言变化，更新请求头
i18n.on("languageChanged", updateAcceptLanguage);

// 创建 QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
    mutations: {
      retry: 1,
    },
  },
});

// 渲染前台应用
const container = document.getElementById("app-root");
if (container) {
  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <BootstrapProvider>
            <NavBackProvider>
              <ErrorBoundary>
                <AppRouter />
              </ErrorBoundary>
            </NavBackProvider>
          </BootstrapProvider>
        </ToastProvider>
      </QueryClientProvider>
    </React.StrictMode>,
  );
}
